/** Operator-facing rule sentences. Stored fields, operator ids and action ids stay unchanged. */
import {
  conditionField,
  operator,
  actionType,
  carrierById,
  servicePickerLabel,
  GROUPS
} from '../config/rateShopConstants';
import { storeLabelForId } from './storeHelpers';
const list = value =>
  (Array.isArray(value) ? value : [value]).filter(
    v => v !== undefined && v !== null && v !== ''
  );
const serviceName = servicePickerLabel;
const carrierName = id => {
  const carrier = carrierById(id);
  return carrier
    ? `${carrier.name} (${carrier.carrierCode})`
    : id || 'a carrier account';
};
export const conditionWord = (field, op) =>
  field?.type === 'number' && op === 'eq'
    ? 'is exactly'
    : field?.type === 'number' && op === 'neq'
    ? 'is not exactly'
    : field?.type === 'list'
    ? {
        eq: 'include exactly',
        neq: 'do not include exactly',
        in: 'include any of',
        not_in: 'include none of',
        contains: 'contain',
        starts_with: 'start with'
      }[op] ||
      operator(op)?.wordLabel ||
      op
    : operator(op)?.wordLabel || op;
export function describeCondition(cond, stores = []) {
  const field = conditionField(cond?.field);
  if (!field) return 'an unknown condition';
  if (field.type === 'bool') {
    const yes = cond.op === 'neq' ? !cond.value : cond.value;
    return yes
      ? 'the address is residential'
      : 'the address is not residential';
  }
  const format = value =>
    field.input === 'store'
      ? storeLabelForId(value, stores)
      : field.unit === '$'
      ? `$${value}`
      : `${value}${field.unit ? ` ${field.unit}` : ''}`;
  const values = list(cond.value);
  const value = values.length
    ? values.map(format).join(cond.op === 'between' ? ' and ' : ' or ')
    : '…';
  return `${field.label} ${conditionWord(field, cond.op)} ${value}`;
}
export function describeMatch(match, stores = []) {
  const all = (match?.all || []).map(c => describeCondition(c, stores));
  const any = (match?.any || []).map(c => describeCondition(c, stores));
  return (
    [
      ...all,
      ...(any.length
        ? [any.length === 1 ? any[0] : `(${any.join(' or ')})`]
        : [])
    ].join(' and ') || 'every order'
  );
}
export function describeAction(action) {
  switch (action?.type) {
    case 'skip':
      return `leave this order alone${
        action.reason ? ` (${action.reason})` : ''
      }`;
    case 'exclude_services':
      return `never use ${
        list(action.services).map(serviceName).join(' or ') || 'these services'
      }`;
    case 'only_services':
      return `only allow ${
        list(action.services).map(serviceName).join(' or ') || 'these services'
      }`;
    case 'exclude_carriers':
      return `never use ${
        list(action.carriers).map(carrierName).join(' or ') ||
        'these carrier accounts'
      }`;
    case 'only_carriers':
      return `only use ${
        list(action.carriers).map(carrierName).join(' or ') ||
        'these carrier accounts'
      }`;
    case 'exclude_groups':
      return `never use ${
        list(action.groups)
          .map(id => GROUPS.find(g => g.id === id)?.label || id)
          .join(' or ') || 'these rating methods'
      }`;
    case 'force_service':
      return `always use ${serviceName(action.serviceCode)} when available${
        action.carrierId ? ` from ${carrierName(action.carrierId)}` : ''
      }`;
    case 'prefer_service': {
      const tolerance = [
        action.withinAmount ? `$${action.withinAmount}` : '',
        action.withinPercent ? `${action.withinPercent}%` : ''
      ]
        .filter(Boolean)
        .join(' or ');
      return `prefer ${serviceName(action.serviceCode)} when it is within ${
        tolerance || 'your price limit'
      } of the cheapest`;
    }
    case 'add_tag':
      return `add the ShipStation tag ${
        list(action.tag || action.tags).join(' or ') || '…'
      }`;
    default:
      return actionType(action?.type)?.label || 'choose an action';
  }
}
export const describeActions = actions =>
  (actions || []).map(describeAction).join('; then ') || 'choose an action';
export const describeRule = (rule, stores = []) =>
  `${
    rule.match?.all?.length || rule.match?.any?.length
      ? `If ${describeMatch(rule.match, stores)}`
      : 'For every order'
  }, then ${describeActions(rule.actions)}.`;

export const shortRuleName = (rule, stores = []) => {
  const first = rule.match?.all?.[0] || rule.match?.any?.[0];
  const shorten = (text, length) =>
    text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text;
  return `${shorten(
    first ? describeCondition(first, stores) : 'Every order',
    28
  )} → ${shorten(describeAction(rule.actions?.[0]), 29)}`;
};

/** The marketplace PO number is the operator's Order #; older logs keep their customer id. */
export const primaryOrderId = decision =>
  decision?.orderNumber || decision?.externalOrderId || decision?.id;
export const secondaryOrderIds = decision =>
  [...new Set([decision?.externalOrderId, decision?.id])].filter(
    id => id && id !== primaryOrderId(decision)
  );

/** Firestore Timestamp | Date | ISO string | null → Date | null */
export const toDate = v => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'object' && typeof v.seconds === 'number')
    return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const money = v =>
  typeof v === 'number' && Number.isFinite(v)
    ? `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`
    : '—';
export const dateTime = value =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : toDate(value)?.toLocaleString() || '—';
